<template>
  <div class="flex items-center justify-center min-h-screen bg-gray-100">
    <div class="w-full max-w-md p-8 bg-white rounded-lg shadow-md">
      <div class="flex items-center justify-center mb-6">
        <div class="w-10 h-10 bg-blue-600 rounded flex items-center justify-center mr-3">
          <span class="text-white font-bold">A+</span>
        </div>
        <h1 class="text-2xl font-semibold text-gray-900">AannemerPlus</h1>
      </div>

      <h2 class="text-lg font-medium text-center text-gray-700 mb-6">
        {{ isCodeMode ? 'Sign in with email code' : isForgotMode ? 'Reset Password' : isRegisterMode ? 'Create Account' : 'Sign In' }}
      </h2>

      <!-- Passwordless email-code login (for owners who built a site via onboarding) -->
      <template v-if="isCodeMode">
        <!-- Step 1: request a code -->
        <form v-if="!codeSent" @submit.prevent="handleRequestCode">
          <p class="text-sm text-gray-600 mb-4">
            Made your website through us? Enter the email you used and we'll send you a login code — no password needed.
          </p>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <InputText v-model="email" type="email" placeholder="you@example.com" class="w-full" />
          </div>
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{{ error }}</div>
          <Button type="submit" label="Send login code" :loading="loading" class="w-full" />
        </form>

        <!-- Step 2: enter the code -->
        <form v-else @submit.prevent="handleVerifyCode">
          <p class="text-sm text-gray-600 mb-4">
            We sent a 6-digit code to <strong>{{ email }}</strong>. Enter it below to sign in.
          </p>
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Login code</label>
            <InputText v-model="code" inputmode="numeric" placeholder="123456" class="w-full" />
          </div>
          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">{{ error }}</div>
          <Button type="submit" label="Sign in" :loading="loading" class="w-full" />
          <div class="mt-3 text-center text-sm text-gray-600">
            <a href="#" class="text-blue-600 hover:underline" @click.prevent="codeSent = false; code = ''; error = ''">Use a different email</a>
          </div>
        </form>

        <div class="mt-4 text-center text-sm text-gray-600">
          <a href="#" class="text-blue-600 hover:underline" @click.prevent="exitCodeMode">Back to Sign In</a>
        </div>
      </template>

      <!-- Forgot Password Mode -->
      <template v-else-if="isForgotMode">
        <form @submit.prevent="handleForgotPassword">
          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <InputText
              v-model="email"
              type="email"
              placeholder="you@example.com"
              class="w-full"
            />
          </div>

          <div v-if="forgotSuccess" class="mb-4 p-3 bg-green-50 border border-green-200 rounded text-green-700 text-sm">
            If an account exists with that email, a reset link has been sent. Check your inbox.
          </div>

          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {{ error }}
          </div>

          <Button
            type="submit"
            label="Send Reset Link"
            :loading="loading"
            class="w-full"
          />
        </form>

        <div class="mt-4 text-center text-sm text-gray-600">
          <a href="#" class="text-blue-600 hover:underline" @click.prevent="isForgotMode = false; error = ''; forgotSuccess = false">Back to Sign In</a>
        </div>
      </template>

      <!-- Login / Register Mode -->
      <template v-else>
        <form @submit.prevent="handleSubmit">
          <div v-if="isRegisterMode" class="flex gap-3 mb-4">
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">First Name</label>
              <InputText
                v-model="firstName"
                placeholder="First name"
                class="w-full"
                :invalid="!!error"
              />
            </div>
            <div class="flex-1">
              <label class="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
              <InputText
                v-model="lastName"
                placeholder="Last name"
                class="w-full"
                :invalid="!!error"
              />
            </div>
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <InputText
              v-model="email"
              type="email"
              placeholder="you@example.com"
              class="w-full"
              :invalid="!!error"
            />
          </div>

          <div class="mb-4">
            <label class="block text-sm font-medium text-gray-700 mb-1">Password</label>
            <Password
              v-model="password"
              :feedback="isRegisterMode"
              toggleMask
              class="w-full"
              inputClass="w-full"
              :invalid="!!error"
            />
          </div>

          <div v-if="!isRegisterMode" class="mb-4 flex justify-between">
            <a href="#" class="text-sm text-blue-600 hover:underline" @click.prevent="enterCodeMode">Sign in with email code</a>
            <a href="#" class="text-sm text-blue-600 hover:underline" @click.prevent="isForgotMode = true; error = ''">Forgot password?</a>
          </div>

          <div v-if="error" class="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {{ error }}
          </div>

          <Button
            type="submit"
            :label="isRegisterMode ? 'Create Account' : 'Sign In'"
            :loading="loading"
            class="w-full"
          />
        </form>

        <div class="mt-4 text-center text-sm text-gray-600">
          <template v-if="isRegisterMode">
            Already have an account?
            <a href="#" class="text-blue-600 hover:underline" @click.prevent="isRegisterMode = false">Sign in</a>
          </template>
          <template v-else>
            Don't have an account?
            <a href="#" class="text-blue-600 hover:underline" @click.prevent="isRegisterMode = true">Create one</a>
          </template>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Button from 'primevue/button'
import { useAuthStore } from '@/stores/auth'
import { apiClient } from '@/api/client'

const router = useRouter()
const authStore = useAuthStore()

const email = ref('')
const password = ref('')
const firstName = ref('')
const lastName = ref('')
const error = ref('')
const loading = ref(false)
const isRegisterMode = ref(false)
const isForgotMode = ref(false)
const forgotSuccess = ref(false)
const isCodeMode = ref(false)
const codeSent = ref(false)
const code = ref('')

function enterCodeMode() {
  isCodeMode.value = true
  isForgotMode.value = false
  codeSent.value = false
  code.value = ''
  error.value = ''
}

function exitCodeMode() {
  isCodeMode.value = false
  codeSent.value = false
  code.value = ''
  error.value = ''
}

async function handleRequestCode() {
  error.value = ''
  loading.value = true
  try {
    await apiClient.requestLoginCode({ email: email.value })
    codeSent.value = true
  } catch (err: any) {
    error.value = err.response?.data?.message || 'An error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleVerifyCode() {
  error.value = ''
  loading.value = true
  try {
    await authStore.loginWithCode(email.value, code.value.trim())
    router.push('/admin')
  } catch (err: any) {
    error.value = err.response?.data?.message || 'Invalid or expired code. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleForgotPassword() {
  error.value = ''
  forgotSuccess.value = false
  loading.value = true

  try {
    await apiClient.forgotPassword({ email: email.value })
    forgotSuccess.value = true
  } catch (err: any) {
    error.value = err.response?.data?.message || 'An error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}

async function handleSubmit() {
  error.value = ''
  loading.value = true

  try {
    if (isRegisterMode.value) {
      if (!firstName.value || !lastName.value) {
        error.value = 'First name and last name are required'
        return
      }
      await authStore.register(email.value, password.value, firstName.value, lastName.value)
    } else {
      await authStore.login(email.value, password.value)
    }
    router.push('/admin')
  } catch (err: any) {
    const message = err.response?.data?.message
    error.value = message || 'An error occurred. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>
